import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { User, LogOut, Settings, Bell } from 'lucide-react'
import { useAuthStore } from '../../core/stores/authStore'
import { useTranslation } from '../../core/hooks/useTranslation'
import { getUserRoleLabel } from '../../core/models/user'
import { UserRole } from '../../core/enums'
import { AppRoute } from '../../core/config/app-routes'
import Button from './Button'
import Badge from './Badge'
import ConfirmationModal from './ConfirmationModal'

export default function UserDropdown() {
  const [isOpen, setIsOpen] = useState(false)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { user, clearAuth } = useAuthStore()
  const { t, isRTL } = useTranslation()

  const getSettingsRoute = () => {
    if (!user) return AppRoute.BUSINESS_OWNER_SETTINGS_ROUTE

    switch (user.userRole) {
      case UserRole.SYSTEM_ADMIN:
        return AppRoute.SYSTEM_ADMIN_SETTINGS_ROUTE
      case UserRole.MONITOR:
        return AppRoute.MONITOR_SETTINGS_ROUTE
      case UserRole.BUSINESS_OWNER:
        return AppRoute.BUSINESS_OWNER_SETTINGS_ROUTE
      case UserRole.BUSINESS_EMPLOYEE:
        return AppRoute.BUSINESS_EMPLOYEE_SETTINGS_ROUTE
      case UserRole.BUSINESS_CASHIER:
        return AppRoute.BUSINESS_CASHIER_SETTINGS_ROUTE
      default:
        return AppRoute.HOME_ROUTE
    }
  }

  const handleSettingsClick = () => {
    setIsOpen(false)
    navigate(getSettingsRoute())
  }

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true)
    setIsOpen(false) // Close dropdown when showing confirmation
  }

  const handleLogoutConfirm = () => {
    clearAuth()
    window.location.href = '/login'
  }

  const handleLogoutCancel = () => {
    setShowLogoutConfirm(false)
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <div className="relative" ref={dropdownRef}>
      {/* User Avatar Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-2 py-1 hover:bg-[#1E293B]/10 rounded-lg"
      >
        <div className="w-7 h-7 bg-[#1E293B] rounded-full flex items-center justify-center shadow-sm">
          <User className="w-4 h-4 text-white" />
        </div>
        <div className="hidden md:block text-left">
          <p className="text-xs font-semibold text-[#1E293B] truncate max-w-24">
            {user?.name}
          </p>
          <p className="text-[11px] text-[#1E293B]/70">
            {user ? getUserRoleLabel(user.userRole) : ''}
          </p>
        </div>
        <svg
          className={`w-4 h-4 text-[#1E293B] transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </Button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} top-full mt-2 w-64 bg-white rounded-2xl shadow-lg border border-[#1E293B]/20 z-50 animate-fade-in`}>
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-[#1E293B]/20">
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 bg-[#1E293B] rounded-full flex items-center justify-center shadow-sm">
                <User className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-[#1E293B] truncate">
                  {user?.name}
                </p>
                <p className="text-[11px] text-[#1E293B]/70">
                  {user ? getUserRoleLabel(user.userRole) : ''}
                </p>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-2">
            {/* Notifications */}
            <button className="hidden w-full flex items-center gap-3 px-4 py-2 text-sm text-[#1E293B] hover:bg-[#1E293B]/10 transition-colors">
              <div className="relative flex items-center justify-center">
                <Bell className="w-4 h-4 text-[#1E293B]" />
                <Badge 
                  variant="danger" 
                  size="sm" 
                  className="absolute -top-2 -right-2 w-4 h-4 flex items-center justify-center p-0 text-xs rounded-full z-10"
                >
                  3
                </Badge>
              </div>
              <span>{t('notifications')}</span>
            </button>

            {/* Settings */}
            <button 
              onClick={handleSettingsClick}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-[#1E293B] hover:bg-[#1E293B]/10 transition-colors"
            >
              <Settings className="w-4 h-4 text-[#1E293B]" />
              <span>{t('settings')}</span>
            </button>

            {/* Divider */}
            <div className="my-2 border-t border-[#1E293B]/20"></div>

            {/* Logout */}
            <button
              onClick={handleLogoutClick}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-[#1E293B] hover:bg-[#1E293B]/10 transition-colors"
            >
              <LogOut className="w-4 h-4 text-[#1E293B]" />
              <span>{t('logout')}</span>
            </button>
          </div>
        </div>
      )}

      {/* Logout Confirmation Modal */}
      <ConfirmationModal
        isOpen={showLogoutConfirm}
        onClose={handleLogoutCancel}
        onConfirm={handleLogoutConfirm}
        title={t('confirmLogout')}
        message={t('confirmLogoutMessage')}
        confirmText={t('logout')}
        cancelText={t('cancel')}
        variant="danger"
        icon={<LogOut className="w-5 h-5 text-red-600" />}
      />
    </div>
  )
}
